import React, { useState, useEffect } from 'react';
import { 
  Briefcase, TrendingUp, TrendingDown, Shield, AlertCircle, 
  Globe, Building, Lightbulb, Clock, Calendar, ChevronRight,
  Target, Award, Compass, Users, Wallet, Star, Zap, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Status colors
const statusColors = {
  growth: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  stable: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/20', dot: 'bg-blue-400' },
  caution: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-400' },
  favorable: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  neutral: { bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/20', dot: 'bg-gray-400' },
};

const CareerInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchCareerData();
  }, []);

  const fetchCareerData = async () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem('birthData');
      let birthParams = {
        year: 1997, month: 5, day: 14, hour: 12, minute: 0,
        latitude: 28.6139, longitude: 77.2090, gender: 'male'
      };
      
      if (stored) {
        const parsed = JSON.parse(stored);
        const dateParts = parsed.date?.split('-') || ['1997', '05', '14'];
        const timeParts = parsed.time?.split(':') || ['12', '00'];
        birthParams = {
          year: parseInt(dateParts[0]),
          month: parseInt(dateParts[1]),
          day: parseInt(dateParts[2]),
          hour: parseInt(timeParts[0]),
          minute: parseInt(timeParts[1]),
          latitude: parsed.latitude || 28.6139,
          longitude: parsed.longitude || 77.2090,
          gender: parsed.gender?.toLowerCase() || 'unknown'
        };
      }
      
      const res = await fetch(`${BACKEND_URL}/api/career/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(birthParams)
      });
      
      if (res.ok) {
        const result = await res.json();
        setData(result.data);
      }
    } catch (e) { 
      console.error('Fetch error:', e); 
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="h-screen bg-[#0a0e14] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-white/40 text-sm">Analyzing career...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-screen bg-[#0a0e14] flex items-center justify-center">
        <p className="text-white/40">Unable to load career data</p>
      </div>
    );
  }

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'story', label: 'Story' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'guidance', label: 'Guidance' },
  ];

  return (
    <div className="h-screen bg-[#0a0e14] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 justify-center mb-1">
          <Briefcase className="w-5 h-5 text-blue-400" />
          <h1 className="text-lg font-semibold text-white">Career & Profession</h1>
        </div>
        <p className="text-white/40 text-[11px] text-center">{data.summary_card?.career_type} Career Path</p>
      </div>

      {/* Section Tabs */}
      <div className="px-4 mb-3">
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all
                ${activeSection === s.id ? 'bg-blue-500/20 text-blue-400' : 'text-white/40'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 overflow-y-auto" style={{ paddingBottom: '100px' }}>
        {activeSection === 'overview' && <OverviewSection data={data} />}
        {activeSection === 'story' && <StorySection data={data} />}
        {activeSection === 'timeline' && <TimelineSection data={data} />}
        {activeSection === 'guidance' && <GuidanceSection data={data} />}
      </div>
    </div>
  );
};

// Overview Section
const OverviewSection = ({ data }) => {
  const currentStatus = data.current_status;
  const jobVsBusiness = data.job_vs_business;
  const foreignCareer = data.foreign_career;
  const careerType = data.career_type;

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl p-4 border border-blue-500/20">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-blue-500/30 flex items-center justify-center flex-shrink-0">
            <Award className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <p className="text-blue-400 text-xs font-medium mb-1">{data.summary_card?.title}</p>
            <p className="text-white text-sm leading-relaxed">{data.summary_card?.message}</p>
          </div>
        </div>
      </div>

      {/* Current Phase */}
      <div className={`rounded-2xl p-4 ${statusColors[currentStatus?.color]?.bg || 'bg-white/5'} border ${statusColors[currentStatus?.color]?.border || 'border-white/10'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {currentStatus?.color === 'green' && <TrendingUp className="w-5 h-5 text-emerald-400" />}
            {currentStatus?.color === 'amber' && <AlertCircle className="w-5 h-5 text-amber-400" />}
            {currentStatus?.color === 'blue' && <Shield className="w-5 h-5 text-blue-400" />}
            <p className="text-white font-medium">{currentStatus?.phase}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${statusColors[currentStatus?.color]?.bg} ${statusColors[currentStatus?.color]?.text}`}>
            Now
          </span>
        </div>
        <p className="text-white/60 text-sm mb-2">{currentStatus?.dasha_theme}</p>
        <p className="text-white/40 text-xs">{currentStatus?.environment}</p>
      </div>

      {/* Career Type Card */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <p className="text-white/50 text-xs font-medium uppercase mb-3">Your Career Profile</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-3">
            <div>
              <p className="text-white/40 text-[10px]">Career Style</p>
              <p className="text-white text-sm font-medium">{careerType?.style}</p>
            </div>
            <div>
              <p className="text-white/40 text-[10px]">Work Preference</p>
              <p className="text-white text-sm font-medium">{careerType?.work_preference}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-white/40 text-[10px]">Growth Pattern</p>
              <p className="text-white text-sm font-medium">{careerType?.growth_pattern}</p>
            </div>
            <div>
              <p className="text-white/40 text-[10px]">Key Strength</p>
              <p className="text-white text-sm font-medium line-clamp-2">{careerType?.strength}</p>
            </div>
          </div>
        </div>
        
        {/* Suitable Fields */}
        <div className="mt-4 pt-3 border-t border-white/5">
          <p className="text-white/40 text-[10px] mb-2">Suitable Fields</p>
          <div className="flex flex-wrap gap-1.5">
            {careerType?.fields?.map((field, i) => (
              <span key={i} className="text-[10px] px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
                {field}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Job vs Business */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-amber-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Job vs Business</p>
        </div>
        <div className={`p-3 rounded-xl ${jobVsBusiness?.recommendation?.includes('Business') ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-500/10 border-blue-500/20'} border`}>
          <p className={`text-sm font-semibold ${jobVsBusiness?.recommendation?.includes('Business') ? 'text-amber-400' : 'text-blue-400'}`}>
            {jobVsBusiness?.recommendation}
          </p>
          <p className="text-white/50 text-xs mt-1">{jobVsBusiness?.explanation}</p>
        </div>
      </div>

      {/* Foreign Career */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-cyan-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Global Opportunities</p>
        </div>
        <div className={`p-3 rounded-xl ${foreignCareer?.indicated ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white/5 border-white/10'} border`}>
          <p className={`text-sm font-semibold ${foreignCareer?.indicated ? 'text-cyan-400' : 'text-white/60'}`}>
            {foreignCareer?.message}
          </p>
          <p className="text-white/50 text-xs mt-1">{foreignCareer?.details}</p>
        </div>
      </div>
    </div>
  );
};

// Story Section
const StorySection = ({ data }) => {
  const pastStory = data.past_story;
  const futureOverview = data.future_overview;

  return (
    <div className="space-y-4">
      {/* Past Story */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-purple-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Your Career Journey</p>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-xl p-4 border border-purple-500/10 mb-4">
          <p className="text-white text-sm leading-relaxed italic">"{pastStory?.narrative}"</p>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-purple-400 text-xs font-medium mb-2">Recurring Themes</p>
            {pastStory?.recurring_themes?.map((theme, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                <p className="text-white/60 text-xs">{theme}</p>
              </div>
            ))}
          </div>
          
          <div>
            <p className="text-purple-400 text-xs font-medium mb-2">Key Turning Points</p>
            {pastStory?.turning_points?.map((point, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <CheckCircle className="w-3 h-3 text-emerald-400" />
                <p className="text-white/60 text-xs">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Future Overview */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-4">
          <Compass className="w-4 h-4 text-cyan-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Future Outlook</p>
        </div>
        
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl p-4 border border-cyan-500/10 mb-4">
          <p className="text-white text-sm leading-relaxed">{futureOverview?.summary}</p>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-emerald-400 text-xs font-medium mb-2">Opportunity Windows</p>
            {futureOverview?.opportunity_windows?.map((opp, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <Star className="w-3 h-3 text-emerald-400" />
                <p className="text-white/60 text-xs">{opp}</p>
              </div>
            ))}
          </div>
          
          {futureOverview?.challenge_periods?.length > 0 && (
            <div>
              <p className="text-amber-400 text-xs font-medium mb-2">Navigate Carefully</p>
              {futureOverview?.challenge_periods?.map((ch, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <AlertCircle className="w-3 h-3 text-amber-400" />
                  <p className="text-white/60 text-xs">{ch}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-white/5">
          <p className="text-cyan-400 text-xs font-medium">{futureOverview?.key_message}</p>
        </div>
      </div>
    </div>
  );
};

// Timeline Section
const TimelineSection = ({ data }) => {
  const [view, setView] = useState('yearly');
  const yearlyTimeline = data.yearly_timeline || [];
  const monthlyTimeline = data.monthly_timeline || [];

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="flex gap-2 bg-white/5 rounded-xl p-1">
        <button
          onClick={() => setView('yearly')}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all
            ${view === 'yearly' ? 'bg-blue-500/20 text-blue-400' : 'text-white/40'}`}
        >
          Yearly
        </button>
        <button
          onClick={() => setView('monthly')}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all
            ${view === 'monthly' ? 'bg-blue-500/20 text-blue-400' : 'text-white/40'}`}
        >
          Monthly
        </button>
      </div>

      {view === 'yearly' ? (
        <div className="space-y-3">
          {yearlyTimeline.map((year, i) => {
            const colors = statusColors[year.status] || statusColors.stable;
            return (
              <div key={i} className={`p-4 rounded-2xl ${colors.bg} border ${colors.border}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${colors.text}`}>{year.year}</span>
                    {year.status === 'growth' && <TrendingUp className={`w-4 h-4 ${colors.text}`} />}
                    {year.status === 'caution' && <AlertCircle className={`w-4 h-4 ${colors.text}`} />}
                    {year.status === 'stable' && <Shield className={`w-4 h-4 ${colors.text}`} />}
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full bg-white/10 ${colors.text} capitalize`}>
                    {year.status}
                  </span>
                </div>
                <p className="text-white text-sm font-medium mb-1">{year.theme}</p>
                <p className="text-white/50 text-xs">{year.focus}</p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
          <div className="space-y-2">
            {monthlyTimeline.map((month, i) => {
              const colors = statusColors[month.status] || statusColors.neutral;
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03]">
                  <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{month.month}</p>
                    <p className="text-white/40 text-[11px]">{month.guidance}</p>
                  </div>
                  <span className={`text-[9px] px-2 py-1 rounded-full ${colors.bg} ${colors.text} capitalize`}>
                    {month.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Guidance Section
const GuidanceSection = ({ data }) => {
  const daily = data.daily_guidance;
  const careerType = data.career_type;

  return (
    <div className="space-y-4">
      {/* Today's Guidance */}
      <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl p-4 border border-amber-500/20">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-amber-400" />
          <p className="text-amber-400 text-xs font-medium uppercase">Today's Guidance</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl bg-amber-500/30 flex flex-col items-center justify-center flex-shrink-0">
            <p className="text-white text-lg font-bold">{new Date().getDate()}</p>
            <p className="text-amber-400 text-[8px] uppercase">{daily?.day?.slice(0, 3)}</p>
          </div>
          <div>
            <p className="text-white text-sm font-medium mb-1">{daily?.tip}</p>
            <p className="text-white/50 text-xs">{daily?.action}</p>
          </div>
        </div>
      </div>

      {/* Career Do's */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Career Do's</p>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-500/10">
            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Focus on {careerType?.style?.toLowerCase()} approach</p>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-500/10">
            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Build expertise in your strength areas</p>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-500/10">
            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Network during favorable periods</p>
          </div>
        </div>
      </div>

      {/* Career Don'ts */}
      <div className="bg-[#12161f] rounded-2xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-white/50 text-xs font-medium uppercase">Career Don'ts</p>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-500/10">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Avoid impulsive job changes during caution periods</p>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-500/10">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Don't expect overnight success</p>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-500/10">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-white/80 text-xs">Avoid conflicts with authority figures</p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-white/[0.02] rounded-xl p-3 border border-white/5">
        <p className="text-white/30 text-[9px] text-center leading-relaxed">
          Career insights are based on Vedic astrology principles. 
          Use as guidance, not guaranteed predictions. 
          All interpretations are proprietary and protected.
        </p>
      </div>
    </div>
  );
};

export default CareerInsights;
